import { TextField } from '@medenterprises/pulse-ui';
import React, { useId } from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors } from 'react-hook-form';
import type { FormText } from 'types/cms';

import usePulseFieldAttributes from './usePulseFieldAttributes';
import { emailRules, fieldId, getFieldError, textRules } from './utils';

type InputType = Omit<FormText, '__component' | 'id'> & {
  type: 'text' | 'email';
};

const TextInput = ({
  control,
  errors,
  identifier,
  label,
  max_length,
  required,
  type = 'text',
}: InputType & { control: Control; errors?: FieldErrors }) => {
  const errorMessage = getFieldError(errors, identifier);
  // Pulse UI's TextField uses its label as the input id, which clashes when labels repeat
  const containerRef = usePulseFieldAttributes('input', { id: fieldId(identifier, useId()) });

  return (
    <Controller
      name={identifier}
      control={control}
      defaultValue=""
      rules={type === 'email' ? emailRules(required) : textRules(required)}
      render={({ onChange, onBlur, value, name }) => (
        <div ref={containerRef}>
          <TextField
            type={type}
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            aria-required={required || undefined}
            aria-invalid={!!errorMessage || undefined}
            error={!!errorMessage}
            errorMessage={errorMessage}
            maxLength={max_length ?? undefined}
            data-testid={identifier}
            autoComplete={type === 'text' ? 'name' : 'email'}
          />
        </div>
      )}
    />
  );
};

export default TextInput;
